//------------------------------------------------------------------------------
// name: PriorityInternalLogin.tsx
// author: mudas
// created: 2025/12/18
//------------------------------------------------------------------------------

import React, { FC } from 'react';
import { useResponsive } from 'ahooks';
import { useLocation, useNavigate } from 'react-router';
import { encodeNextPath, getNextPath, isOutPlatform, loginByOpenId } from 'common/hooks';
import { getEnv } from 'common/utils';

import { ROUTER_PATHNAME } from 'common/router/routes';

import { ConfigProvider, Divider, Image } from '@fuxi/eevee-ui';
import { MailOutlined, MobileOutlined } from '@ant-design/icons';
import { GradientButton, SecondaryButton } from 'common/components';
import MyIcon from 'common/components/EeveeIcon';
import { LoginWrapper } from './CommonLogin';

import LogoTiangongDark from 'common/assets/image/logo/logo-tiangong-dark.png';

export interface IProps {
  // 登录页根路径类型（主要用于区分在对外环境包含对内、对外两种风格登录界面）
  rootPath?: ROUTER_PATHNAME;
}

const PriorityInternalLogin: FC<IProps> = ({ rootPath }) => {
  const { isInternal } = getEnv();
  const loginRoot = rootPath || ROUTER_PATHNAME.LOGIN;

  const responsive = useResponsive();
  const nav = useNavigate();
  const location = useLocation();

  const handleLoginByOpenId = () => {
    const { search, origin } = window.location;
    const next = getNextPath();
    const params = new URLSearchParams(search);

    params.set('next', encodeNextPath(next));

    let url: string;

    if (isOutPlatform(params)) {
      // 外部平台直接跳转目标页面
      url = `${origin}/${ROUTER_PATHNAME.OPENID_LOGIN}?${params.toString()}`;
    } else {
      // 天工对外平台先跳转到中转页面，对内平台直接跳转路由地址
      url = !isInternal ? `${origin}/${ROUTER_PATHNAME.OPENID_LOGIN}?${params.toString()}` : `${origin}${next ?? '/'}`;
    }

    loginByOpenId(url);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            controlHeight: 54,
            borderRadius: 54,
            contentFontSize: 20,
            paddingBlock: 13,

            controlHeightLG: 80,
            borderRadiusLG: 80,
            contentFontSizeLG: 30,
            paddingBlockLG: 22,
          },
        },
      }}>
      <LoginWrapper closedURL="/">
        <div className="flex flex-col items-center gap-8 mt-12 mb-30">
          <Image preview={false} src={LogoTiangongDark} alt="logo" className="logo" />

          <GradientButton
            type="primary"
            block
            size={responsive.xl ? 'large' : undefined}
            icon={
              <MyIcon
                size={24}
                icon="open-id"
                style={{ filter: 'invert(1) grayscale(1) contrast(500%)', marginBottom: -4 }}
              />
            }
            onClick={handleLoginByOpenId}>
            网易员工/供应商
          </GradientButton>
        </div>

        <div>
          <Divider className="text-color-tertiary! text-size-xs!">更多方式</Divider>
          <SecondaryButton
            block
            size={responsive.xl ? 'large' : undefined}
            icon={<MobileOutlined className="text-size-16px" />}
            onClick={() => nav(`/${loginRoot}/mobile${location.search}`, { replace: true })}>
            手机号登录
          </SecondaryButton>
          <SecondaryButton
            block
            className="mt-4"
            size={responsive.xl ? 'large' : undefined}
            icon={<MailOutlined className="text-size-16px" />}
            onClick={() => nav(`/${loginRoot}/mail${location.search}`, { replace: true })}>
            邮箱登录
          </SecondaryButton>
        </div>
      </LoginWrapper>
    </ConfigProvider>
  );
};

export default PriorityInternalLogin;
